import { useMemo } from 'react';
import { DashboardData } from '../services/dashboard-rh.service';

export function useDashboardCalculations(
  dashboard: DashboardData | undefined,
  selectedEstablishment: string | null = null
) {
  return useMemo(() => {
    if (!dashboard) {
      return {
        filteredIndicators: [],
        evolutions: {
          masseSalariale: 0,
          coutGlobal: 0,
          absenteisme: 0,
          ecartRemuneration: 0,
        },
        lastMonth: null,
        summary: {
          moyenneMasseSalarialeBrute: 0,
          moyenneCoutGlobalEmployeur: 0,
          moyenneTauxAbsenteisme: 0,
          moyenneTauxTurnover: 0,
          moyenneEcartRemunerationPct: 0,
        },
        formatCurrency: (value: number) => value.toLocaleString('fr-FR'),
        formatPercent: (value: number) => `${value.toFixed(2)}%`,
      };
    }

    // Filter indicators based on selected establishment
    // If selectedEstablishment is null, we want the global view (where establishmentCode is null)
    // Wait, the user said "We generate for all establishments".
    // Does the global view exist as a separate record with null establishmentCode?
    // Yes, my backend logic creates one record with null code for the global view.
    // And one record per establishment.
    // So filtering is simple: match the code.
    const filteredIndicators = (dashboard.monthlyIndicators || []).filter(
      (m) => m.establishmentCode === selectedEstablishment
    );

    // Calculate summary
    const validMonths = filteredIndicators.filter(
      (m) => m.masseSalarialeBrute !== null
    );
    
    const summary =
      validMonths.length > 0
        ? {
            moyenneMasseSalarialeBrute:
              validMonths.reduce(
                (sum, m) => sum + (m.masseSalarialeBrute || 0),
                0
              ) / validMonths.length,
            moyenneCoutGlobalEmployeur:
              validMonths.reduce(
                (sum, m) => sum + (m.coutGlobalEmployeur || 0),
                0
              ) / validMonths.length,
            moyenneTauxAbsenteisme:
              validMonths.reduce((sum, m) => sum + (m.tauxAbsenteisme || 0), 0) /
              validMonths.length,
            moyenneTauxTurnover:
              validMonths.reduce((sum, m) => sum + (m.tauxTurnover || 0), 0) /
              validMonths.length,
            moyenneEcartRemunerationPct:
              validMonths.reduce(
                (sum, m) => sum + (m.ecartRemunerationPct || 0),
                0
              ) / validMonths.length,
          }
        : {
            moyenneMasseSalarialeBrute: 0,
            moyenneCoutGlobalEmployeur: 0,
            moyenneTauxAbsenteisme: 0,
            moyenneTauxTurnover: 0,
            moyenneEcartRemunerationPct: 0,
          };

    const { previousPeriodSummary } = dashboard;

    // Calculate evolution vs previous period
    // Note: Previous period summary might not be filtered correctly if it comes pre-calculated from backend?
    // The backend currently doesn't return previousPeriodSummary in the new logic?
    // I didn't check `getDashboardData` for `previousPeriodSummary`.
    // The `DashboardData` interface has it.
    // But my `getDashboardData` implementation in `dashboard.service.ts` only returns `monthlyData`.
    // It seems `previousPeriodSummary` was calculated on frontend before?
    // No, `DashboardData` has `previousPeriodSummary`.
    // Let's check `dashboard.service.ts` again.
    // It returns:
    /*
    return {
      uuid: generation.uuid,
      ...
      monthlyData: flatMonthlyData,
      status: generation.status,
      errorMessage: generation.errorMessage,
    };
    */
    // It does NOT return `summary` or `previousPeriodSummary`.
    // The frontend service `dashboard-rh.service.ts` was calculating `summary`.
    // It was NOT calculating `previousPeriodSummary`.
    // Wait, let's check `dashboard-rh.service.ts` again.
    // It calculates `summary`.
    // It does NOT calculate `previousPeriodSummary`.
    // So `previousPeriodSummary` must be undefined in the current frontend logic unless it comes from backend?
    // The `DashboardData` interface has it as optional.
    // `useDashboardCalculations` uses it.
    // If it's missing, evolutions are 0.
    // So I don't need to worry about it for now, or I should calculate it too if I had data.
    // But I only have current period data.

    const calculateEvolution = (current: number, previous: number): number => {
      if (previous === 0) return 0;
      return ((current - previous) / previous) * 100;
    };

    const evolutions = {
      masseSalariale: previousPeriodSummary
        ? calculateEvolution(
            summary.moyenneMasseSalarialeBrute,
            previousPeriodSummary.moyenneMasseSalarialeBrute
          )
        : 0,
      coutGlobal: previousPeriodSummary
        ? calculateEvolution(
            summary.moyenneCoutGlobalEmployeur,
            previousPeriodSummary.moyenneCoutGlobalEmployeur
          )
        : 0,
      absenteisme: previousPeriodSummary
        ? calculateEvolution(
            summary.moyenneTauxAbsenteisme,
            previousPeriodSummary.moyenneTauxAbsenteisme
          )
        : 0,
      ecartRemuneration: previousPeriodSummary
        ? calculateEvolution(
            summary.moyenneEcartRemunerationPct,
            previousPeriodSummary.moyenneEcartRemunerationPct
          )
        : 0,
    };

    const lastMonth = filteredIndicators[filteredIndicators.length - 1];

    const formatCurrency = (value: number | null | undefined) => {
      if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
      }
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    };

    const formatPercent = (value: number | null | undefined) => {
      if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
      }
      return `${value.toFixed(2)}%`;
    };

    return {
      filteredIndicators,
      summary,
      evolutions,
      lastMonth,
      formatCurrency,
      formatPercent,
    };
  }, [dashboard, selectedEstablishment]);
}

